/**
 * useChatbot Hook
 * Wrapper around Redux state and actions for chatbot
 */

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/shared/store/store';
import {
  toggleChat,
  minimizeChat,
  closeChat,
  sendMessageRequest,
  clearMessages,
  openHistory,
  closeHistory,
  selectConversation,
  createConversationRequest,
  fetchConversationsRequest,
  renameConversationRequest,
  deleteConversationRequest,
} from '../redux/chatbot-slice';

export const useChatbot = () => {
  const dispatch = useDispatch();
  const chatbotState = useSelector((state: RootState) => state.chatbot);

  const handleToggleChat = useCallback(() => {
    dispatch(toggleChat());
  }, [dispatch]);

  const handleMinimizeChat = useCallback(() => {
    dispatch(minimizeChat());
  }, [dispatch]);

  const handleCloseChat = useCallback(() => {
    dispatch(closeChat());
  }, [dispatch]);

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!content.trim()) return;
      dispatch(
        sendMessageRequest({
          message: content.trim(),
          conversationId: chatbotState.currentConversationId || undefined,
        }),
      );
    },
    [dispatch, chatbotState.currentConversationId],
  );

  const handleClearMessages = useCallback(() => {
    dispatch(clearMessages());
  }, [dispatch]);

  const handleOpenHistory = useCallback(() => {
    dispatch(openHistory());
    dispatch(fetchConversationsRequest(undefined));
  }, [dispatch]);

  const handleCloseHistory = useCallback(() => {
    dispatch(closeHistory());
  }, [dispatch]);

  const handleSelectConversation = useCallback(
    (conversation: any) => {
      dispatch(selectConversation(conversation));
    },
    [dispatch],
  );

  const handleStartNewConversation = useCallback(() => {
    dispatch(createConversationRequest());
  }, [dispatch]);

  const handleRenameConversation = useCallback(
    (conversationId: string, newTitle: string) => {
      dispatch(
        renameConversationRequest({
          conversationId,
          newTitle,
        }),
      );
    },
    [dispatch],
  );

  const handleDeleteConversation = useCallback(
    (conversationId: string) => {
      dispatch(deleteConversationRequest(conversationId));
    },
    [dispatch],
  );

  const handleFetchConversations = useCallback(
    (params?: { page?: number; limit?: number; search?: string }) => {
      dispatch(fetchConversationsRequest(params));
    },
    [dispatch],
  );

  return {
    // State
    messages: chatbotState.messages,
    config: chatbotState.config,
    isLoading: chatbotState.isLoading,
    isSending: chatbotState.isSending,
    error: chatbotState.error,
    conversations: chatbotState.conversations,
    isHistoryOpen: chatbotState.isHistoryOpen,
    isHistoryLoading: chatbotState.isHistoryLoading,
    currentConversationId: chatbotState.currentConversationId,
    currentConversationTitle: chatbotState.currentConversationTitle,
    isWebSocketConnected: chatbotState.isWebSocketConnected,
    lastResponseMetadata: chatbotState.lastResponseMetadata,

    // Actions
    toggleChat: handleToggleChat,
    minimizeChat: handleMinimizeChat,
    closeChat: handleCloseChat,
    sendMessage: handleSendMessage,
    clearMessages: handleClearMessages,
    openHistory: handleOpenHistory,
    closeHistory: handleCloseHistory,
    selectConversation: handleSelectConversation,
    startNewConversation: handleStartNewConversation,
    renameConversation: handleRenameConversation,
    deleteConversation: handleDeleteConversation,
    fetchConversations: handleFetchConversations,

    // Aliases for backward compatibility
    setIsHistoryOpen: (open: boolean) => {
      if (open) {
        handleOpenHistory();
      } else {
        handleCloseHistory();
      }
    },
    createNewConversation: handleStartNewConversation,
    conversationName: chatbotState.currentConversationTitle,
    selectedConversationId: chatbotState.currentConversationId,
  };
};
